import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { Box } from '@mui/material';
import { CardHeader } from '@apigrowfinance/pbolt-card';
import { ChipButton } from '@apigrowfinance/pbolt-chip-button';
import { Switch } from '@apigrowfinance/pbolt-switch';
import Tag from '@apigrowfinance/pbolt-tag';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import dayjs from 'dayjs';
import { RiTimeLine, RiEqualizerLine } from 'react-icons/ri';
import { useParams } from 'react-router-dom';

import { GetCardControlsResponse } from '@clients/domains/flexControls/flexControlAccountClient';
import {
    CustomerFlexControl,
    FlexControl,
} from '@clients/domains/flexControls/flexControlsClient';
import usePatchUpdateAccountFlexControl from '@hooks/queries/usePatchUpdateAccountFlexControl';
import usePatchUpdateCustomerFlexControl from '@hooks/queries/usePatchUpdateCustomerFlexControl';
import flexControls from '@messages/flexControls/flexControls';
import getEnumTranslation from '@utils/getEnumTranslation';
import { toTitleCase } from '@utils/string/caseUtils';

export type CardControlPageHeaderProps = {
    data: GetCardControlsResponse;
    isActive: boolean;
    isLoading: boolean;
    setShowEditModal: Dispatch<SetStateAction<boolean>>;
};

const ControlPageHeader = ({
    data,
    isActive,
    isLoading,
    setShowEditModal,
}: CardControlPageHeaderProps) => {
    const [isSwitchChecked, setIsSwitchChecked] = useState(isActive);
    const { accountId } = useParams();
    const { theme } = useTheme();
    const { mutate: mutateAccount } = usePatchUpdateAccountFlexControl({
        accountId: parseFloat(accountId),
        flexControlId: data?.id,
        onError: () => {
            // revert optimistic update
            setIsSwitchChecked(!isSwitchChecked);
        },
    });

    const { mutate: mutateCustomer } = usePatchUpdateCustomerFlexControl({
        accountId: parseFloat(accountId),
        customerId: data?.customer_id,
        flexControlId: data?.id,
        onError: () => {
            // revert optimistic update
            setIsSwitchChecked(!isSwitchChecked);
        },
    });

    const disabledClick = (event: React.SyntheticEvent) => {
        event.stopPropagation();
        const optimisticUpdate = !isSwitchChecked;
        setIsSwitchChecked(optimisticUpdate);
        if (data?.customer_id) {
            mutateCustomer({
                active: optimisticUpdate,
            } as CustomerFlexControl);
        } else {
            mutateAccount({
                active: optimisticUpdate,
            } as FlexControl);
        }
    };

    useEffect(() => {
        setIsSwitchChecked(isActive);
    }, [isActive]);

    const ResetLabel = () => {
        return (
            <span style={{ marginRight: 3 }}>
                {getEnumTranslation('resets', {
                    messages: flexControls,
                    values: { day: dayjs().to(data?.reset_datetime) },
                })}
            </span>
        );
    };

    return (
        <CardHeader
            icon={<RiEqualizerLine />}
            title={data?.name}
            desc={toTitleCase(data?.type)}
            isLoading={isLoading}
            secondaryAction={
                <Box sx={{ justifyContent: 'end' }}>
                    <Tag
                        label={<ResetLabel />}
                        icon={<RiTimeLine />}
                        color="default"
                        filled
                    />
                    <Switch
                        onClick={disabledClick}
                        checked={isSwitchChecked}
                        onLabel={getEnumTranslation('enabled')}
                        offLabel={getEnumTranslation('disabled')}
                        data-testid="flex-control-switch"
                        sx={{ ml: theme.spacing(1.5) }}
                    />
                    <ChipButton
                        label={getEnumTranslation('edit')}
                        onClick={() => setShowEditModal(true)}
                        variant="editMenu"
                        isLoading={isLoading}
                        bordered
                    />
                </Box>
            }
        />
    );
};

export default ControlPageHeader;
